import * as React from 'react';
import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@hetivo/hetivo-lib-web-utils';
import { useIsMobile } from '@hetivo/hetivo-lib-web-utils';
import { useDebouncedValue } from '@hetivo/hetivo-lib-front-utils';
import { useTranslation } from 'react-i18next';
import type { CurrentUserResponse } from '@hetivo/hetivo-lib-types';
import { useMasterContext } from '&/master';
import type { PanelView } from './main-selector-dropdown-content';
import { MainSelectorDropdownContent } from './main-selector-dropdown-content';

// ─── Props ────────────────────────────────────────────────────────────────────

interface MainSelectorDropdownProps {
  backendUser: CurrentUserResponse | null;
  canReadLocations: boolean;
  canReadTerritories: boolean;
  children: React.ReactNode;
  hasMultipleTerritories: boolean;
  singleTerritoryId: string | null;
  territoryName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface InitialDropdownState {
  browsedTerritoryId: string | null;
  browsedTerritoryName: string | null;
  panel: PanelView;
}

interface ResolveInitialDropdownStateParams {
  currentTerritoryId: string | null;
  isSingleTerritoryImposed: boolean;
  resolvedTerritoryName: string | null;
  singleTerritoryId: string | null;
}

function resolveInitialDropdownState({
  currentTerritoryId,
  isSingleTerritoryImposed,
  resolvedTerritoryName,
  singleTerritoryId,
}: ResolveInitialDropdownStateParams): InitialDropdownState {
  if (isSingleTerritoryImposed) {
    return {
      browsedTerritoryId: singleTerritoryId,
      browsedTerritoryName: resolvedTerritoryName,
      panel: 'locations',
    };
  }
  if (currentTerritoryId) {
    return {
      browsedTerritoryId: currentTerritoryId,
      browsedTerritoryName: resolvedTerritoryName,
      panel: 'locations',
    };
  }
  return { browsedTerritoryId: null, browsedTerritoryName: null, panel: 'territories' };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MainSelectorDropdown({
  backendUser,
  canReadLocations,
  canReadTerritories,
  children,
  hasMultipleTerritories,
  singleTerritoryId,
  territoryName,
}: MainSelectorDropdownProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const { territoryId: currentTerritoryId } = useMasterContext();

  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<PanelView>('territories');
  const [browsedTerritoryId, setBrowsedTerritoryId] = useState<string | null>(null);
  const [browsedTerritoryName, setBrowsedTerritoryName] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Single-territory imposed: the user only has one territory, so the dropdown always
  // opens directly to the locations panel with no ability to go back to territories.
  const isSingleTerritoryImposed = !hasMultipleTerritories && !!singleTerritoryId;

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen);
    if (nextOpen) {
      // Reset search on open
      setSearchQuery('');
      const resolvedTerritoryName = territoryName ?? null;
      const initial = resolveInitialDropdownState({
        currentTerritoryId,
        isSingleTerritoryImposed,
        resolvedTerritoryName,
        singleTerritoryId,
      });
      setPanel(initial.panel);
      setBrowsedTerritoryId(initial.browsedTerritoryId);
      setBrowsedTerritoryName(initial.browsedTerritoryName);
    }
    // Don't reset state on close — let it hold until next open to avoid flash during close animation
  };

  const handleBrowseTerritory = (territoryId: string, name: string): void => {
    setBrowsedTerritoryId(territoryId);
    setBrowsedTerritoryName(name);
    setPanel('locations');
  };

  const handleGoBackToTerritories = (): void => {
    setPanel('territories');
  };

  const handleClose = (): void => {
    handleOpenChange(false);
  };

  const contentProps = {
    backendUser,
    browsedTerritoryId,
    browsedTerritoryName,
    canReadLocations,
    canReadTerritories,
    canSelectTerritory: canReadTerritories,
    debouncedQuery,
    isSingleTerritoryImposed,
    onBrowseTerritory: handleBrowseTerritory,
    onClose: handleClose,
    onGoBackToTerritories: handleGoBackToTerritories,
    onSearchChange: setSearchQuery,
    panel,
    searchQuery,
  };

  if (isMobile) {
    return (
      <Drawer onOpenChange={handleOpenChange} open={open}>
        <DrawerTrigger asChild>{children}</DrawerTrigger>
        <DrawerContent className="max-h-[85vh]">
          <DrawerTitle className="sr-only">{t('mainSelector.drawerTitle')}</DrawerTitle>
          <MainSelectorDropdownContent {...contentProps} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" alignOffset={-4} className="w-[360px] p-0 overflow-hidden" sideOffset={8}>
        <MainSelectorDropdownContent {...contentProps} />
      </PopoverContent>
    </Popover>
  );
}
